// Taxas.tsx
//
// Aba "Taxas", de "Descontos e Taxas". Cadastra a REGRA de cada taxa do turno
// de caixa: nome, natureza, forma + valor, base de cálculo e se incide antes do
// desconto.
//
// Três decisões desta tela merecem ser lidas antes do código:
//
//  1. `forma` e `valor` são DOIS campos, como em Descontos. "%" e "R$" nunca no
//     mesmo campo de texto — em produção a mesma coluna hoje guarda "3" e "10%".
//  2. Escolher a natureza "couvert" aplica o padrão JÁ DECIDIDO pelo dono do
//     produto (base = subtotal, incide antes do desconto), e a tela DIZ que foi
//     um padrão e de onde ele veio. Valor que aparece sozinho no campo vira "o
//     sistema mandou", e ninguém descobre que pode mudar.
//  3. `entraNoFaturamento` e `entraNaBaseDeImposto` nascem VAZIOS — terceiro
//     estado, não `false`. A decisão é do contador do cliente. Um `false` de
//     brinde seria o sistema classificando fiscalmente em nome dele.
//
// Onde isso iria: acd."ActPeriodTypeInfo", periodTypeInfoType 2 ("taxs"). A
// tabela e o tipo existem; a ROTA não. O Salvar monta o payload certo, manda
// para o shell e mostra a recusa nomeada que volta.

import React, { useEffect, useMemo, useRef, useState } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/descontosSection";
import {
  AVISO_ARITMETICA,
  FormTaxa,
  OPCOES_BASE_TAXA,
  OPCOES_FORMA,
  OPCOES_NATUREZA_TAXA,
  PADRAO_COUVERT,
  PERIOD_TYPE_INFO_TAXAS,
  ROTA_QUE_FALTA,
  Tom,
  TurnoDeCaixa,
  aplicarRegra,
  contarLegadas,
  lerTurnosDeCaixa,
  mostrarADefinir,
  mostrarForma,
  mostrarValor,
  payloadDeRegras,
  regraDaTaxa,
  rotuloDe,
  taxaVazia,
  validarTaxa,
} from "./tipos";

type AvisoLocal = { tom: Tom; texto: string; itens?: string[] };

interface Props {
  decryptedInfoToken: any;
  handleSave: (objectType: string, payload?: any) => any;
}

const Taxas: React.FC<Props> = ({ decryptedInfoToken, handleSave }) => {
  const turnos: TurnoDeCaixa[] = useMemo(
    () => lerTurnosDeCaixa(decryptedInfoToken, PERIOD_TYPE_INFO_TAXAS),
    [decryptedInfoToken]
  );

  const [formularios, setFormularios] = useState<Record<string, FormTaxa>>({});
  const [regrasPorTurno, setRegrasPorTurno] = useState<Record<string, any[]>>({});
  const [avisos, setAvisos] = useState<Record<string, AvisoLocal>>({});
  const [salvando, setSalvando] = useState<Record<string, boolean>>({});

  // Guarda de unmount: o salvamento é assíncrono e o usuário pode trocar de aba
  // no meio dele.
  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  useEffect(() => {
    if (turnos.length === 0) return;
    const formulariosIniciais: Record<string, FormTaxa> = {};
    const regrasIniciais: Record<string, any[]> = {};
    const avisosIniciais: Record<string, AvisoLocal> = {};

    for (const turno of turnos) {
      formulariosIniciais[turno.uuid] = taxaVazia();
      regrasIniciais[turno.uuid] = turno.regras;
      if (turno.corrompida) {
        avisosIniciais[turno.uuid] = {
          tom: "atencao",
          texto:
            "O que está gravado neste turno não é um JSON válido, então as " +
            "taxas não puderam ser lidas. Elas não aparecem na tabela abaixo — " +
            "e continuam no banco do jeito que estão.",
        };
      }
    }

    setFormularios(formulariosIniciais);
    setRegrasPorTurno(regrasIniciais);
    setAvisos(avisosIniciais);
  }, [turnos]);

  const mudar = (uuid: string, mudanca: Partial<FormTaxa>) => {
    setFormularios((atuais) => ({
      ...atuais,
      [uuid]: { ...(atuais[uuid] ?? taxaVazia()), ...mudanca },
    }));
  };

  /**
   * Trocar a natureza para "couvert" traz junto o padrão do dono do produto.
   * Ele é aplicado UMA vez, na troca — depois disso o campo é do usuário, e a
   * tela mostra quando o padrão foi mudado em vez de reimpor a escolha dele.
   */
  const mudarNatureza = (uuid: string, natureza: string) => {
    if (natureza === "couvert") {
      mudar(uuid, {
        natureza: natureza as any,
        base: PADRAO_COUVERT.base,
        incideAntesDoDesconto: PADRAO_COUVERT.incideAntesDoDesconto,
      });
      return;
    }
    mudar(uuid, { natureza: natureza as any });
  };

  /** Traz uma taxa gravada de volta para o formulário, para corrigir/completar. */
  const editar = (uuid: string, regra: any) => {
    setFormularios((atuais) => ({
      ...atuais,
      [uuid]: {
        id: regra?.id ?? "",
        nome: regra?.nome ?? "",
        natureza: regra?.natureza ?? "servico",
        forma: regra?.forma === "reais" ? "reais" : "percentual",
        valor: regra?.valor != null ? String(regra.valor) : "",
        base: regra?.base ?? "subtotal",
        incideAntesDoDesconto: regra?.incideAntesDoDesconto === true,
        // `null` gravado quer dizer "o contador ainda não decidiu". Trazer isso
        // de volta como "não" apagaria uma pergunta em aberto.
        entraNoFaturamento:
          regra?.entraNoFaturamento === true
            ? "sim"
            : regra?.entraNoFaturamento === false
            ? "nao"
            : "",
        entraNaBaseDeImposto:
          regra?.entraNaBaseDeImposto === true
            ? "sim"
            : regra?.entraNaBaseDeImposto === false
            ? "nao"
            : "",
      },
    }));
  };

  const salvar = async (turno: TurnoDeCaixa) => {
    const uuid = turno.uuid;
    const form = formularios[uuid] ?? taxaVazia();

    const problemas = validarTaxa(form);
    if (problemas.length > 0) {
      setAvisos((atuais) => ({
        ...atuais,
        [uuid]: {
          tom: "erro",
          texto: "Corrija antes de salvar:",
          itens: problemas,
        },
      }));
      return;
    }

    if (!uuid) {
      setAvisos((atuais) => ({
        ...atuais,
        [uuid]: {
          tom: "erro",
          texto:
            "Este turno de caixa veio do token sem identificador " +
            "(periodTypeUuid). Nada foi enviado.",
        },
      }));
      return;
    }

    const regras = aplicarRegra(regrasPorTurno[uuid] ?? [], regraDaTaxa(form));
    const payload = payloadDeRegras(turno, regras, PERIOD_TYPE_INFO_TAXAS);

    setSalvando((atuais) => ({ ...atuais, [uuid]: true }));
    try {
      const resposta = await handleSave("taxa", payload);
      if (!montado.current) return;

      if (resposta && resposta.ok === false) {
        setAvisos((atuais) => ({
          ...atuais,
          [uuid]: { tom: resposta.tom ?? "erro", texto: resposta.texto },
        }));
        return;
      }

      if (resposta && resposta.ok === true) {
        setAvisos((atuais) => ({
          ...atuais,
          [uuid]: { tom: "ok", texto: resposta.texto ?? "Taxa gravada." },
        }));
        setRegrasPorTurno((atuais) => ({ ...atuais, [uuid]: regras }));
        setFormularios((atuais) => ({ ...atuais, [uuid]: taxaVazia() }));
        return;
      }

      // Nem ok nem recusa: o shell não disse o que aconteceu, e silêncio não é
      // sucesso.
      setAvisos((atuais) => ({
        ...atuais,
        [uuid]: {
          tom: "atencao",
          texto:
            "O salvamento não devolveu resposta, então não dá para afirmar que " +
            `algo foi gravado. Enquanto a rota ${ROTA_QUE_FALTA} não existir, ` +
            "trate a taxa como não gravada.",
        },
      }));
    } catch (erro: any) {
      if (!montado.current) return;
      setAvisos((atuais) => ({
        ...atuais,
        [uuid]: {
          tom: "erro",
          texto:
            erro?.response?.data?.message ??
            erro?.message ??
            "Não foi possível salvar a taxa.",
        },
      }));
    } finally {
      if (montado.current) {
        setSalvando((atuais) => ({ ...atuais, [uuid]: false }));
      }
    }
  };

  if (!decryptedInfoToken) {
    return (
      <AlfredoStyles.FieldsContainer>
        <AlfredoStyles.LoadingContainer>
          <AlfredoStyles.LoadingSpinner />
          <div>Carregando turnos de caixa…</div>
        </AlfredoStyles.LoadingContainer>
      </AlfredoStyles.FieldsContainer>
    );
  }

  return (
    <AlfredoStyles.FieldsContainer>
      <AlfredoStyles.Header>
        <AlfredoStyles.HeaderContent>
          <AlfredoStyles.HeaderTitle>
            <AlfredoStyles.MaterialIcons
              style={{ fontSize: "calc(var(--u) * 36)" }}
            >
              receipt_long
            </AlfredoStyles.MaterialIcons>
            Taxas
          </AlfredoStyles.HeaderTitle>
          <AlfredoStyles.HeaderSubtitle>
            Serviço, couvert, entrega e embalagem de cada turno de caixa
          </AlfredoStyles.HeaderSubtitle>
        </AlfredoStyles.HeaderContent>
      </AlfredoStyles.Header>

      <AlfredoStyles.AvisoGrande>
        <AlfredoStyles.MaterialIcons
          style={{ fontSize: "calc(var(--u) * 28)" }}
        >
          warning_amber
        </AlfredoStyles.MaterialIcons>
        <div>
          <AlfredoStyles.AvisoGrandeTitulo>
            Isto cadastra a regra, não conserta a conta
          </AlfredoStyles.AvisoGrandeTitulo>
          {AVISO_ARITMETICA}
        </div>
      </AlfredoStyles.AvisoGrande>

      {/* Antes de qualquer digitação: onde este formulário iria parar, e por
          que ele ainda não vai. */}
      <AlfredoStyles.Aviso $tom="atencao">
        <strong>Nada nesta tela grava ainda.</strong> As taxas iriam para{" "}
        <code>acd."ActPeriodTypeInfo"</code>, tipo {PERIOD_TYPE_INFO_TAXAS} (
        <code>taxs</code>) — a tabela e o tipo já existem, mas{" "}
        <strong>nenhum router do backend expõe {ROTA_QUE_FALTA}</strong>. Salvar
        monta o payload correto e recebe uma recusa nomeada: nada é enviado ao
        servidor e nada fica guardado no navegador. O que você digitar se perde
        ao sair da aba.
      </AlfredoStyles.Aviso>

      {turnos.length === 0 ? (
        <AlfredoStyles.EmptyState>
          <AlfredoStyles.EmptyStateIcon>🧾</AlfredoStyles.EmptyStateIcon>
          <AlfredoStyles.EmptyStateTitle>
            Nenhum turno de caixa cadastrado
          </AlfredoStyles.EmptyStateTitle>
          <AlfredoStyles.EmptyStateDescription>
            Taxa pertence a um turno de caixa. Cadastre um em Lojas → Períodos
            para poder criar taxas aqui.
          </AlfredoStyles.EmptyStateDescription>
        </AlfredoStyles.EmptyState>
      ) : (
        turnos.map((turno) => {
          const uuid = turno.uuid;
          const form = formularios[uuid] ?? taxaVazia();
          const regras = regrasPorTurno[uuid] ?? [];
          const aviso = avisos[uuid];
          const legadas = contarLegadas(regras);

          // Couvert com o padrão intacto x couvert alterado pelo usuário. As
          // duas frases dizem de onde o valor veio; a segunda existe para o
          // dono saber que ele mesmo mudou, e não o sistema.
          const couvertNoPadrao =
            form.natureza === "couvert" &&
            form.base === PADRAO_COUVERT.base &&
            form.incideAntesDoDesconto === PADRAO_COUVERT.incideAntesDoDesconto;

          return (
            <AlfredoStyles.Card key={uuid || turno.nome}>
              <AlfredoStyles.CardTitulo>
                <AlfredoStyles.MaterialIcons>
                  schedule
                </AlfredoStyles.MaterialIcons>
                {turno.loja} — {turno.nome}
              </AlfredoStyles.CardTitulo>
              <AlfredoStyles.CardSubtitulo>
                {turno.existe
                  ? `${regras.length} taxa(s) lida(s) do que está gravado`
                  : "Este turno ainda não tem nenhuma taxa gravada"}
              </AlfredoStyles.CardSubtitulo>

              {/* É aqui que o defeito aparece cru: o que está gravado hoje é
                  `{"Taxa de Entrega": "3,00", "Couver": "10%"}` — reais e
                  percentual no mesmo campo, sem nada dizendo qual é qual. */}
              {legadas > 0 && (
                <AlfredoStyles.Aviso $tom="atencao">
                  {legadas} taxa(s) deste turno estão no formato antigo: o banco
                  guarda só o nome e um texto, e é justamente aí que "3,00" (em
                  reais) e "10%" (percentual) convivem no mesmo campo. Elas
                  aparecem abaixo com o texto exato que está gravado, e não dá
                  para editá-las aqui — adivinhar a unidade transformaria
                  R$ 3,00 de entrega em 3% da conta, calado. Recadastre a taxa
                  pelo formulário, escolhendo a forma.
                </AlfredoStyles.Aviso>
              )}

              {/* ---------------- O QUE ESTÁ GRAVADO ---------------- */}
              <AlfredoStyles.Bloco>
                <AlfredoStyles.BlocoTitulo>
                  <AlfredoStyles.MaterialIcons
                    style={{ fontSize: "var(--fs-base)" }}
                  >
                    list_alt
                  </AlfredoStyles.MaterialIcons>
                  Taxas gravadas (leitura)
                </AlfredoStyles.BlocoTitulo>

                {regras.length === 0 ? (
                  <AlfredoStyles.VazioNaTabela>
                    Nenhuma taxa para mostrar.
                  </AlfredoStyles.VazioNaTabela>
                ) : (
                  <AlfredoStyles.Tabela>
                    <thead>
                      <tr>
                        <AlfredoStyles.TabelaCabecalho>
                          Nome
                        </AlfredoStyles.TabelaCabecalho>
                        <AlfredoStyles.TabelaCabecalho>
                          Natureza
                        </AlfredoStyles.TabelaCabecalho>
                        <AlfredoStyles.TabelaCabecalho>
                          Forma
                        </AlfredoStyles.TabelaCabecalho>
                        <AlfredoStyles.TabelaCabecalho>
                          Valor
                        </AlfredoStyles.TabelaCabecalho>
                        <AlfredoStyles.TabelaCabecalho>
                          Base
                        </AlfredoStyles.TabelaCabecalho>
                        <AlfredoStyles.TabelaCabecalho>
                          Antes do desconto
                        </AlfredoStyles.TabelaCabecalho>
                        <AlfredoStyles.TabelaCabecalho>
                          Faturamento
                        </AlfredoStyles.TabelaCabecalho>
                        <AlfredoStyles.TabelaCabecalho>
                          Base de imposto
                        </AlfredoStyles.TabelaCabecalho>
                        <AlfredoStyles.TabelaCabecalho> </AlfredoStyles.TabelaCabecalho>
                      </tr>
                    </thead>
                    <tbody>
                      {regras.map((regra: any, indice: number) =>
                        regra?.legado === true ? (
                          // Formato antigo: só nome e um texto. As colunas que
                          // o dado não tem ficam em "—", nunca num palpite.
                          <tr key={regra?.id ?? indice}>
                            <AlfredoStyles.TabelaCelula>
                              {regra.nome}
                            </AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>—</AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>—</AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>
                              <code>{regra.valorBruto}</code>
                              <AlfredoStyles.Dica>
                                texto exato que está gravado
                              </AlfredoStyles.Dica>
                            </AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>—</AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>—</AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>—</AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>—</AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>
                              <AlfredoStyles.Dica>
                                formato antigo
                              </AlfredoStyles.Dica>
                            </AlfredoStyles.TabelaCelula>
                          </tr>
                        ) : (
                          <tr key={regra?.id ?? indice}>
                            <AlfredoStyles.TabelaCelula>
                              {regra?.nome ?? "—"}
                            </AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>
                              {rotuloDe(OPCOES_NATUREZA_TAXA, regra?.natureza)}
                            </AlfredoStyles.TabelaCelula>
                            {/* Forma e valor em colunas separadas, sempre. */}
                            <AlfredoStyles.TabelaCelula>
                              {mostrarForma(regra?.forma)}
                            </AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>
                              {mostrarValor(regra?.forma, regra?.valor)}
                            </AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>
                              {rotuloDe(OPCOES_BASE_TAXA, regra?.base)}
                            </AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>
                              {regra?.incideAntesDoDesconto === true
                                ? "sim"
                                : "não"}
                            </AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>
                              {mostrarADefinir(regra?.entraNoFaturamento)}
                            </AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>
                              {mostrarADefinir(regra?.entraNaBaseDeImposto)}
                            </AlfredoStyles.TabelaCelula>
                            <AlfredoStyles.TabelaCelula>
                              <AlfredoStyles.TabelaAcao
                                type="button"
                                onClick={() => editar(uuid, regra)}
                              >
                                <AlfredoStyles.MaterialIcons
                                  style={{ fontSize: "var(--fs-sm)" }}
                                >
                                  edit
                                </AlfredoStyles.MaterialIcons>
                                Editar
                              </AlfredoStyles.TabelaAcao>
                            </AlfredoStyles.TabelaCelula>
                          </tr>
                        )
                      )}
                    </tbody>
                  </AlfredoStyles.Tabela>
                )}
              </AlfredoStyles.Bloco>

              {/* ---------------- FORMULÁRIO ---------------- */}
              <AlfredoStyles.Bloco>
                <AlfredoStyles.BlocoTitulo>
                  <AlfredoStyles.MaterialIcons
                    style={{ fontSize: "var(--fs-base)" }}
                  >
                    {form.id ? "edit" : "add_circle_outline"}
                  </AlfredoStyles.MaterialIcons>
                  {form.id ? "Editando uma taxa" : "Nova taxa"}
                </AlfredoStyles.BlocoTitulo>

                <AlfredoStyles.Grade>
                  <AlfredoStyles.Campo $largo>
                    <AlfredoStyles.Rotulo htmlFor={`nome-taxa-${uuid}`}>
                      Nome da taxa
                    </AlfredoStyles.Rotulo>
                    <AlfredoStyles.Entrada
                      id={`nome-taxa-${uuid}`}
                      type="text"
                      value={form.nome}
                      placeholder="Ex.: Taxa de serviço"
                      onChange={(e) => mudar(uuid, { nome: e.target.value })}
                    />
                  </AlfredoStyles.Campo>

                  <AlfredoStyles.Campo>
                    <AlfredoStyles.Rotulo htmlFor={`natureza-${uuid}`}>
                      Natureza
                    </AlfredoStyles.Rotulo>
                    <AlfredoStyles.Seletor
                      id={`natureza-${uuid}`}
                      value={form.natureza}
                      onChange={(e) => mudarNatureza(uuid, e.target.value)}
                    >
                      {OPCOES_NATUREZA_TAXA.map((opcao) => (
                        <option key={opcao.valor} value={opcao.valor}>
                          {opcao.rotulo}
                        </option>
                      ))}
                    </AlfredoStyles.Seletor>
                    {form.natureza === "couvert" && (
                      <AlfredoStyles.Dica>
                        <AlfredoStyles.Etiqueta>
                          padrão do couvert
                        </AlfredoStyles.Etiqueta>{" "}
                        {couvertNoPadrao
                          ? "base = subtotal da conta e incidência antes do desconto foram preenchidos por este padrão, decidido pelo dono do produto. Dá para mudar os dois abaixo."
                          : "o padrão do dono do produto é base = subtotal da conta e incidência antes do desconto; você mudou pelo menos um dos dois."}
                      </AlfredoStyles.Dica>
                    )}
                  </AlfredoStyles.Campo>

                  {/* FORMA e VALOR são dois campos, sempre. */}
                  <AlfredoStyles.Campo>
                    <AlfredoStyles.Rotulo htmlFor={`forma-taxa-${uuid}`}>
                      Forma
                    </AlfredoStyles.Rotulo>
                    <AlfredoStyles.Seletor
                      id={`forma-taxa-${uuid}`}
                      value={form.forma}
                      onChange={(e) =>
                        mudar(uuid, { forma: e.target.value as any })
                      }
                    >
                      {OPCOES_FORMA.map((opcao) => (
                        <option key={opcao.valor} value={opcao.valor}>
                          {opcao.rotulo}
                        </option>
                      ))}
                    </AlfredoStyles.Seletor>
                  </AlfredoStyles.Campo>

                  <AlfredoStyles.Campo>
                    <AlfredoStyles.Rotulo htmlFor={`valor-taxa-${uuid}`}>
                      Valor
                    </AlfredoStyles.Rotulo>
                    <AlfredoStyles.CampoComUnidade>
                      <AlfredoStyles.Unidade>
                        {form.forma === "percentual" ? "%" : "R$"}
                      </AlfredoStyles.Unidade>
                      <AlfredoStyles.Entrada
                        id={`valor-taxa-${uuid}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.valor}
                        onChange={(e) => mudar(uuid, { valor: e.target.value })}
                      />
                    </AlfredoStyles.CampoComUnidade>
                    <AlfredoStyles.Dica>
                      Só o número. A unidade vem do campo "Forma".
                    </AlfredoStyles.Dica>
                  </AlfredoStyles.Campo>

                  <AlfredoStyles.Campo>
                    <AlfredoStyles.Rotulo htmlFor={`base-${uuid}`}>
                      Base de cálculo
                    </AlfredoStyles.Rotulo>
                    <AlfredoStyles.Seletor
                      id={`base-${uuid}`}
                      value={form.base}
                      onChange={(e) =>
                        mudar(uuid, { base: e.target.value as any })
                      }
                    >
                      {OPCOES_BASE_TAXA.map((opcao) => (
                        <option key={opcao.valor} value={opcao.valor}>
                          {opcao.rotulo}
                        </option>
                      ))}
                    </AlfredoStyles.Seletor>
                    <AlfredoStyles.Dica>
                      "Por pessoa" e "valor fixo" só fazem sentido em reais —
                      percentual precisa de um total para incidir.
                    </AlfredoStyles.Dica>
                  </AlfredoStyles.Campo>

                  <AlfredoStyles.Campo>
                    <AlfredoStyles.Rotulo>Incidência</AlfredoStyles.Rotulo>
                    <AlfredoStyles.Linha>
                      <AlfredoStyles.Caixa
                        type="checkbox"
                        checked={form.incideAntesDoDesconto}
                        onChange={(e) =>
                          mudar(uuid, {
                            incideAntesDoDesconto: e.target.checked,
                          })
                        }
                      />
                      Incide antes do desconto
                    </AlfredoStyles.Linha>
                    <AlfredoStyles.Dica>
                      Marcado, a taxa é calculada sobre a conta cheia; desmarcado,
                      sobre a conta já descontada.
                    </AlfredoStyles.Dica>
                  </AlfredoStyles.Campo>

                  {/* Os dois campos do contador. Ficam juntos, com o mesmo
                      rótulo de pendência, para não parecer que um deles já foi
                      resolvido. */}
                  <AlfredoStyles.Campo>
                    <AlfredoStyles.Rotulo htmlFor={`faturamento-${uuid}`}>
                      Entra no faturamento?
                    </AlfredoStyles.Rotulo>
                    <AlfredoStyles.Seletor
                      id={`faturamento-${uuid}`}
                      value={form.entraNoFaturamento}
                      onChange={(e) =>
                        mudar(uuid, {
                          entraNoFaturamento: e.target.value as any,
                        })
                      }
                    >
                      <option value="">a definir com o contador</option>
                      <option value="sim">sim</option>
                      <option value="nao">não</option>
                    </AlfredoStyles.Seletor>
                    <AlfredoStyles.Dica>
                      Nasce em branco de propósito: a decisão está aberta e o
                      sistema não escolhe por você. Em branco vai como "não
                      definido", nunca como "não".
                    </AlfredoStyles.Dica>
                  </AlfredoStyles.Campo>

                  <AlfredoStyles.Campo>
                    <AlfredoStyles.Rotulo htmlFor={`imposto-${uuid}`}>
                      Entra na base de imposto?
                    </AlfredoStyles.Rotulo>
                    <AlfredoStyles.Seletor
                      id={`imposto-${uuid}`}
                      value={form.entraNaBaseDeImposto}
                      onChange={(e) =>
                        mudar(uuid, {
                          entraNaBaseDeImposto: e.target.value as any,
                        })
                      }
                    >
                      <option value="">a definir com o contador</option>
                      <option value="sim">sim</option>
                      <option value="nao">não</option>
                    </AlfredoStyles.Seletor>
                    <AlfredoStyles.Dica>
                      Também em branco de propósito. Classificação fiscal é
                      decisão do contador do cliente.
                    </AlfredoStyles.Dica>
                  </AlfredoStyles.Campo>
                </AlfredoStyles.Grade>

                <AlfredoStyles.AcaoRodape>
                  <AlfredoStyles.Button
                    type="button"
                    disabled={salvando[uuid]}
                    onClick={() => salvar(turno)}
                  >
                    <AlfredoStyles.MaterialIcons
                      style={{ fontSize: "var(--fs-base)" }}
                    >
                      save
                    </AlfredoStyles.MaterialIcons>
                    {salvando[uuid] ? "Salvando…" : "Salvar taxa"}
                  </AlfredoStyles.Button>

                  {form.id && (
                    <AlfredoStyles.ButtonSecundario
                      type="button"
                      onClick={() =>
                        setFormularios((atuais) => ({
                          ...atuais,
                          [uuid]: taxaVazia(),
                        }))
                      }
                    >
                      Cancelar edição
                    </AlfredoStyles.ButtonSecundario>
                  )}
                </AlfredoStyles.AcaoRodape>

                {aviso && (
                  <AlfredoStyles.Aviso $tom={aviso.tom}>
                    {aviso.texto}
                    {aviso.itens && (
                      <AlfredoStyles.AvisoLista>
                        {aviso.itens.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </AlfredoStyles.AvisoLista>
                    )}
                  </AlfredoStyles.Aviso>
                )}
              </AlfredoStyles.Bloco>
            </AlfredoStyles.Card>
          );
        })
      )}
    </AlfredoStyles.FieldsContainer>
  );
};

export default Taxas;
